import './popupUpsert.scss';

import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { confirm } from "devextreme/ui/dialog";
import { Form, Button, Popup, ScrollView, DataGrid, Toast, RadioGroup, CheckBox, SelectBox } from "devextreme-react";
import { DataGridRef, Column } from "devextreme-react/data-grid";
import { FormRef, Item, GroupItem } from "devextreme-react/form";
import { Position, ToolbarItem } from "devextreme-react/popup";
import { ClickEvent } from 'devextreme/ui/button';
import { useScreenSize } from '../../../../utils/media-query';

import { ATS_RapportoInterventoPersonaleEdit } from '..';
import { PopupPersonaleEditHandle } from './popupPersonaleEdit';

import useApiConfiguration from '../../../../apiConfig';
import {
    DDTAttiviDTO,
    PersonaleRapportinoDTO, PersonaleRapportinoDTOFromJSON, PersonaleRapportinoDTOToJSON,
    RapportiniApi, RapportiniCreateDTO
} from '../../../../data';
import { useClienteDataSource } from '../../../../datasources/clienteDataSource';

export type PopupCreateHandle = {
    show: (tipologia: string) => void
};

type Props = {
    onChange: () => void
};

type State = {
    tipologia: string,
    visible: boolean
};

const PopupCreate: React.ForwardRefRenderFunction<PopupCreateHandle, Props> = (props, ref) => {
    const emptyData: RapportiniCreateDTO = {
        idCommessa: null,
        idCliente: undefined,
        tipoRapportino: null,
        dataRapportino: new Date(),
        descrizioneAttivita: null,
        ubicazioneTest: null,
        descrizioneLavori: null,
        strumentiMisura: null,
        isRelazioneProva: false,
        isSchedaTecnica: false,
        riferimentoOrdine: null,
        luogo: null,
        personaRiferimento: null,
        isTrimestrale: false,
        isSemestrale: false,
        isAnnuale: false,
        isManutenzioneStraordinaria: false,
        dataRiferimentoOrdine: null,
        riferimentoDDT: null,
        dataRiferimentoDDT: null,
        attrezzatureSpeciali: null,
        isCheckList: false,
        isReportAnomalie: false,
        isReportIntervento: false,
        personaleRapportino: []
    };

    const formRef = useRef<FormRef>(null);
    const personaleGridRef = useRef<DataGridRef>(null);
    const popupPersonaleEditRef = useRef<PopupPersonaleEditHandle>(null);

    const apiConfig = useApiConfiguration();
    const { isLarge, isMedium, isSmall, isXSmall } = useScreenSize();
    const clienteDataSource = useClienteDataSource();

    const [state, setState] = useState<State>({ tipologia: '', visible: false });
    const [toastConfig, setToastConfig] = useState({ isVisible: false, message: '' } as { isVisible: boolean, message: string });
    const [formData, setFormData] = useState<RapportiniCreateDTO>();
    const [clienteId, setClienteId] = useState<number | null>(null);
    const [personaleLst, setPersonaleLst] = useState<PersonaleRapportinoDTO[]>([]);
    const [ddtDataSource, setDdtDataSource] = useState<DDTAttiviDTO[]>([]);
    const [canChangeTestata, setCanChangeTestata] = useState(true);
    const [canChangePersInter, setCanChangePersInter] = useState(false);

    const toTime = (d: Date | string) => new Date(d).getTime();

    function hideInfo() {
        setState({ tipologia: '', visible: false });
    };

    useImperativeHandle(ref, () => ({
        show(tipologia) {
            setState({ tipologia: tipologia, visible: true });
        }
    }));

    useEffect(() => {
        if (state.visible) {
            setPersonaleLst([]);

            setFormData({
                ...emptyData,
                tipoRapportino: state.tipologia
            });
        }
    }, [state.tipologia, state.visible]);

    const onHidingToast = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false
        });
    }, [toastConfig, setToastConfig]);

    const onFieldChanged = (e) => {
        const { dataField, value } = e;

        if (dataField === "idCliente" && value !== clienteId) {
            setClienteId(value || null);
            formData.riferimentoDDT = undefined;
            formData.dataRiferimentoDDT = null;
        }
    };

    const handleChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const insert = (e: ClickEvent) => {
        const validate = formRef.current?.instance().validate();

        if (validate?.isValid) {
            if ((personaleLst ?? []).length == 0) {
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    message: 'Inserire il personale'
                });
            } else {
                formData.personaleRapportino = personaleLst;

                var api = new RapportiniApi(apiConfig);
                api.insertRapportinoRaw({
                    rapportiniCreateDTO: formData
                })
                    .then(() => props.onChange())
                    .finally(() => hideInfo());
            }
        }
    };

    const openPersonale = (rowIdx: number) => {
        if (rowIdx == -1) {
            if (personaleLst.length > 0) {
                var obj = personaleLst.find((_, idx) => idx === 0);
                var json = JSON.stringify(PersonaleRapportinoDTOToJSON(obj));
                popupPersonaleEditRef.current.show(json, -1, formData.idCliente, false);
            } else
                popupPersonaleEditRef.current.show('', -1, formData.idCliente, false);
        } else {
            var obj = personaleLst.find((_, idx) => idx === rowIdx);
            var json = JSON.stringify(PersonaleRapportinoDTOToJSON(obj));
            popupPersonaleEditRef.current.show(json, rowIdx, formData.idCliente, true);
        }
    };

    const upsertPersonale = (json: string, idx: number) => {
        const objNew = PersonaleRapportinoDTOFromJSON(JSON.parse(json));

        setPersonaleLst(prev => {
            const list = prev ?? [];
            if (idx === -1 || idx < 0 || idx >= list.length)
                return [...list, objNew];

            const next = list.slice();
            next[idx] = objNew;
            return next;
        });
    };

    function checkDeletePersonale(rowIndex: number) {
        const result = confirm("Confermi l'eliminazione?", "Conferma");
        result.then((dialogRes) => {
            if (dialogRes)
                deletePersonale(rowIndex);
        });
    };

    function deletePersonale(rowIndex: number) {
        setPersonaleLst(prev => {
            const list = prev ?? [];
            return list.filter((_, idx) => idx !== rowIndex);
        });

        personaleGridRef.current?.instance().refresh();
    };

    useEffect(() => {
        setCanChangeTestata((personaleLst ?? []).length == 0);
    }, [personaleLst]);

    useEffect(() => {
        setCanChangePersInter(clienteId != null && clienteId != undefined);

        if (clienteId) {
            var api = new RapportiniApi(apiConfig);
            api.getDDTAttivi({
                clienteId: clienteId
            }).then((res) => setDdtDataSource(res));
        } else
            setDdtDataSource([]);
    }, [clienteId]);

    return (
        <React.Fragment>
            <Popup
                enableBodyScroll={false}
                visible={state.visible}
                dragEnabled={false}
                hideOnOutsideClick={true}
                showCloseButton={true}
                fullScreen={(isSmall || isXSmall ? true : false)}
                showTitle={true}
                title={"Nuovo Rapporto M. 111"}
                container=".dx-viewport"
                height={"85%"}
                onHidden={hideInfo}
            >
                <Position at="center" my="center" of="" collision="fit" />

                <ToolbarItem widget="dxButton" toolbar="bottom" location="center">
                    <Button
                        onClick={insert}
                        icon="check"
                        text="Salva"
                        type="default"
                        stylingMode="contained"
                    />
                </ToolbarItem>

                <ScrollView className='rapportino-create-scroll'>
                    <Form
                        ref={formRef} formData={formData}
                        labelMode='floating' labelLocation='left'
                        colCount={(isLarge || isMedium) ? 2 : 1}
                        style={{ padding: "10px" }}
                        onFieldDataChanged={onFieldChanged}
                    >
                        <Item visible={false} dataField='tipoRapportino' />

                        <Item colSpan={1} dataField='idCliente' label={{ text: 'Cliente' }}
                            editorType='dxSelectBox' isRequired={true}
                            editorOptions={{
                                disabled: !canChangeTestata,
                                dataSource: clienteDataSource,
                                displayExpr: 'ragioneSociale',
                                valueExpr: 'idCliente',
                                searchEnabled: true,
                                showClearButton: true
                            }}
                        />

                        <Item colSpan={1} itemType='empty' visible={(isLarge || isMedium)} />

                        <Item colSpan={1} dataField="personaRiferimento" label={{ text: "Persona Riferimento" }} editorType="dxTextBox" isRequired={true} />

                        <Item colSpan={1} dataField="luogo" label={{ text: "Luogo" }} editorType="dxTextBox" isRequired={true} />

                        <GroupItem caption="Controllo Periodico" colSpan={(isLarge || isMedium) ? 2 : 1} colCount={4}>
                            <Item colSpan={(isLarge || isMedium) ? 3 : 4}>
                                <div className='custom-controlloperiodico'>
                                    <RadioGroup
                                        items={["Trimestrale", "Semestrale", "Annuale"]}
                                        layout='horizontal'
                                        value={
                                            formData?.isTrimestrale ? "Trimestrale"
                                                : formData?.isSemestrale ? "Semestrale"
                                                    : formData?.isAnnuale ? "Annuale" : null
                                        }
                                        onValueChanged={(e) => {
                                            const v = e.value;

                                            setFormData((prev) => ({
                                                ...prev,
                                                isTrimestrale: v === "Trimestrale",
                                                isSemestrale: v === "Semestrale",
                                                isAnnuale: v === "Annuale"
                                            }));
                                        }}
                                    />

                                    <CheckBox text="Controlli non distruttivi"
                                        value={formData?.isControlliNonDistruttivi ?? false}
                                        onValueChanged={(e) => handleChange("isControlliNonDistruttivi", e.value)}
                                    />

                                    <CheckBox text="Manutenzione Straordinaria"
                                        value={formData?.isManutenzioneStraordinaria ?? false}
                                        onValueChanged={(e) => handleChange('isManutenzioneStraordinaria', e.value)}
                                    />
                                </div>
                            </Item>
                        </GroupItem>

                        <GroupItem caption='Riferimento Ordine' colSpan={1} colCount={2}>
                            <Item colSpan={1} dataField='riferimentoOrdine' label={{ text: "N." }} editorType='dxTextBox' />

                            <Item colSpan={1} dataField="dataRiferimentoOrdine" label={{ text: "Del" }} editorType="dxDateBox"
                                editorOptions={{
                                    type: "date",
                                    displayFormat: (isLarge || isMedium) ? "EEEE d MMMM yyyy" : "dd/MM/yyyy",
                                    openOnFieldClick: true
                                }}
                            />
                        </GroupItem>

                        <GroupItem caption='Materiali Impiegati' colSpan={1} colCount={2}>
                            <Item colSpan={2} label={{ text: "DDT" }} dataField='riferimentoDDT'
                                editorType='dxSelectBox' isRequired={formData?.isManutenzioneStraordinaria ?? false}
                                editorOptions={{
                                    disabled: !clienteId,
                                    dataSource: ddtDataSource,
                                    displayExpr: 'descrizione',
                                    valueExpr: 'numero',
                                    searchEnabled: true,
                                    showClearButton: true,
                                    onValueChanged: ((e) => {
                                        const obj = ddtDataSource.find(x => x.numero === e.value);
                                        formData.dataRiferimentoDDT = obj?.data ?? null;
                                    })
                                }}
                            />
                        </GroupItem>

                        <Item colSpan={(isLarge || isMedium) ? 2 : 1} dataField="attrezzatureSpeciali" label={{ text: "Attrezzature Speciali utilizzate" }}
                            editorType="dxTextArea" cssClass="dx-textarea-custom"
                            editorOptions={{
                                maxLength: 250,
                                autoResizeEnabled: true
                            }}
                        />

                        <Item colSpan={(isLarge || isMedium) ? 2 : 1}>
                            <div style={{ display: "flex", justifyContent: "end", marginTop: "10px", marginBottom: "10px" }}>
                                <Button disabled={!canChangePersInter} icon="add" text='Aggiungi Personale' stylingMode='text' onClick={() => openPersonale(-1)} />
                            </div>

                            <DataGrid
                                className='grid theme-dependent' ref={personaleGridRef} dataSource={personaleLst}
                                rowAlternationEnabled defaultFocusedRowIndex={0} noDataText='Nessun personale caricato'
                            >
                                <Column
                                    width={100}
                                    cellRender={(cellData) => (
                                        <div style={{ display: "flex" }}>
                                            <Button disabled={!canChangePersInter} icon="edit" hint="Modifica" stylingMode='text' onClick={() => openPersonale(cellData.rowIndex)} />
                                            <Button disabled={!canChangePersInter} icon="trash" hint="Elimina" stylingMode='text' onClick={() => checkDeletePersonale(cellData.rowIndex)} />
                                        </div>
                                    )}
                                />
                                <Column dataField='dataIntervento' caption='Data' />
                                <Column dataField="personale" />
                                <Column dataField="commessa" />
                                <Column dataField="oreTotali" />
                            </DataGrid>
                        </Item>
                    </Form>
                </ScrollView>
            </Popup>

            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                height={45} width={200}
                position={{ at: 'center', my: 'center', of: window, offset: { x: 0, y: 0 } }}
                type='error' displayTime={3000}
                onHidden={onHidingToast}
                animation={{ show: { type: 'fade', duration: 400, from: 0, to: 1 }, hide: { type: 'fade', duration: 400, to: 0 } }}
            />

            <ATS_RapportoInterventoPersonaleEdit ref={popupPersonaleEditRef} onChange={upsertPersonale} />
        </React.Fragment>
    );
}

export default React.forwardRef(PopupCreate);